import { renderNode, type GeometryMap, type Measurer, type NodeGeometry, type RenderTreeOptions, type ResolvedNode, type SectionGeometry, type TableRowGeometry } from "@broadpaper/core";
import { vnodeToDom } from "./dom.js";

/** FNV-1a 32-bit hash; fast and good enough for cache keys. */
export function hashString(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36) + s.length.toString(36);
}

export function sectionHash(section: ResolvedNode, width: number, mode: string): string {
  return hashString(JSON.stringify(section)) + ":" + Math.round(width * 100) + ":" + mode;
}

/**
 * Measures resolved sections by laying them out as continuous galleys in a
 * hidden container and reading geometry from the DOM. Results are cached by
 * section content hash so unchanged sections are never re-measured.
 */
export class DomMeasurer implements Measurer {
  private readonly host: HTMLElement;
  private readonly cache = new Map<string, SectionGeometry>();
  private opts: RenderTreeOptions | null = null;
  /** What the measurements since the last `settled()` found still on its way. */
  private arriving: Array<Promise<unknown>> = [];

  constructor(private readonly doc: Document, container?: HTMLElement) {
    this.host = doc.createElement("div");
    this.host.className = "bp-doc bp-galley";
    this.host.setAttribute("aria-hidden", "true");
    (container ?? doc.body).appendChild(this.host);
  }

  setOptions(opts: RenderTreeOptions): void {
    this.opts = opts;
    this.host.classList.toggle("bp-doc--design", opts.mode === "design");
  }

  measure(sections: ResolvedNode[], width: number): GeometryMap {
    if (!this.opts) throw new Error("DomMeasurer.setOptions() must be called before measure()");
    const out: GeometryMap = {};
    const pending: Array<{ section: ResolvedNode; key: string; el: HTMLElement }> = [];
    for (const section of sections) {
      const key = sectionHash(section, width, this.opts.mode);
      const hit = this.cache.get(key);
      if (hit) {
        out[section.key] = hit;
        continue;
      }
      const el = this.doc.createElement("div");
      el.className = "bp-galley__section";
      el.style.width = `${width}px`;
      el.style.position = "relative";
      const v = renderNode(section, { ...this.opts, width }, width);
      if (v) el.appendChild(vnodeToDom(v, this.doc));
      pending.push({ section, key, el });
    }
    if (pending.length === 0) return out;

    // Single layout pass: attach all, then read.
    for (const p of pending) this.host.appendChild(p.el);
    const read = pending.map((p) => ({ p, geom: readSectionGeometry(p.el, p.section, this.opts!) }));

    /*
     * Whether this was measured too early, asked straight after reading.
     *
     * An image with no stated height is 0px until it has decoded, and text is laid
     * out in a fallback face until the face it names has loaded — and reading the
     * geometry is what makes the browser start fetching both. So a document
     * measured the moment it is shown was paginated as shorter than it draws: an
     * imported invoice's logo measured nothing, its text a line short per block,
     * and the preview's second page ended ninety pixels above the last rows of the
     * table. Such a measurement is still returned, because something has to be
     * drawn now, but it is never cached, and `settled()` tells the caller when to
     * measure again. A face loading is a fact about the document rather than one
     * section, so it keeps the whole batch out of the cache.
     */
    const fonts = (this.doc as Document & { fonts?: FontFaceSet }).fonts;
    const faces: Array<Promise<unknown>> = [];
    if (fonts && typeof fonts[Symbol.iterator] === "function") for (const face of fonts) if (face.status === "loading") faces.push(face.loaded);
    this.arriving.push(...faces);
    for (const { p, geom } of read) {
      const images = [...p.el.querySelectorAll("img")].filter((img) => !img.complete).map(decoded);
      this.arriving.push(...images);
      if (!faces.length && !images.length) this.cache.set(p.key, geom);
      out[p.section.key] = geom;
    }
    for (const p of pending) p.el.remove();
    if (this.cache.size > 400) {
      // Simple eviction: drop the oldest half.
      const keys = [...this.cache.keys()].slice(0, 200);
      for (const k of keys) this.cache.delete(k);
    }
    return out;
  }

  /**
   * Resolves once everything the measurements since the last call found still
   * arriving has arrived — or failed to, which is final too — with whether there
   * was anything. When it is true, a layout taken before it is short and should be
   * taken again; the sections involved were not cached, so the next measurement
   * reads them afresh.
   */
  async settled(): Promise<boolean> {
    const waiting = this.arriving;
    this.arriving = [];
    if (!waiting.length) return false;
    await Promise.all(waiting.map((p) => p.catch(() => undefined)));
    return true;
  }

  clearCache(): void {
    this.cache.clear();
  }

  destroy(): void {
    this.host.remove();
    this.cache.clear();
    this.arriving = [];
  }
}

/**
 * When an image has decoded, or failed to.
 *
 * `decode()` works on an element that is no longer in the document, which is what
 * a galley element is by the time anybody waits. A broken image counts as arrived:
 * its box is final, and it reports `complete`, so it is not waited for twice.
 *
 * The failure is caught here, where the promise is made, and not in `settled()`. A
 * caller that never asks — the Chromium backend lays out once — would otherwise leave
 * a blocked image's rejection unhandled, and the render service reports an unhandled
 * rejection as a failed document: a private-network image, refused by design, stopped
 * the whole PDF with "The source image cannot be decoded".
 */
function decoded(img: HTMLImageElement): Promise<unknown> {
  if (typeof img.decode === "function") return img.decode().catch(() => undefined);
  return new Promise((resolve) => {
    img.addEventListener("load", resolve, { once: true });
    img.addEventListener("error", resolve, { once: true });
  });
}

function readSectionGeometry(root: HTMLElement, section: ResolvedNode, opts: RenderTreeOptions): SectionGeometry {
  const base = root.getBoundingClientRect();
  const nodes: Record<string, NodeGeometry> = {};
  const byKey = new Map<string, ResolvedNode>();
  const collect = (n: ResolvedNode) => {
    byKey.set(n.key, n);
    for (const c of n.children) collect(c);
  };
  collect(section);

  const elements = root.querySelectorAll<HTMLElement>("[data-key]");
  for (const el of elements) {
    const key = el.getAttribute("data-key")!;
    if (nodes[key]) continue; // first occurrence wins
    const node = byKey.get(key);
    const r = el.getBoundingClientRect();
    const g: NodeGeometry = { top: r.top - base.top, height: r.height, left: r.left - base.left, width: r.width };
    if (node) {
      const def = opts.registry.get(node.type);
      const measure = def?.measure ?? (node.flow.split === "lines" ? "text" : node.flow.split === "rows" ? "table" : "none");
      if (measure === "text") g.lines = readLineBoxes(el, base.top, g.top + g.height);
      else if (measure === "table") g.rows = readTableRows(el, base.top);
    }
    nodes[key] = g;
  }
  const sectionRect = root.firstElementChild?.getBoundingClientRect();
  const height = sectionRect ? sectionRect.height : base.height;
  return { height, width: base.width, nodes };
}

/** Returns the y coordinate (relative to `baseTop`) of the bottom of each line box. */
export function readLineBoxes(el: HTMLElement, baseTop: number, nodeBottom: number): number[] {
  const container = (el.querySelector("[data-part='text']") as HTMLElement | null) ?? el;
  const doc = el.ownerDocument;
  const rects: Array<{ top: number; bottom: number }> = [];
  const walker = doc.createTreeWalker(container, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: (n) => {
      if (n.nodeType === Node.TEXT_NODE) return (n.textContent ?? "").trim().length ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
      const tag = (n as Element).tagName;
      if (tag === "BR" || tag === "IMG" || tag === "SVG") return NodeFilter.FILTER_ACCEPT;
      const e = n as HTMLElement;
      if (e.classList?.contains("bp-p--empty")) return NodeFilter.FILTER_ACCEPT;
      return NodeFilter.FILTER_SKIP;
    }
  });
  let cur: Node | null = walker.nextNode();
  while (cur) {
    if (cur.nodeType === Node.TEXT_NODE) {
      const range = doc.createRange();
      range.selectNodeContents(cur);
      for (const r of range.getClientRects()) if (r.height > 0 && r.width > 0) rects.push({ top: r.top - baseTop, bottom: r.bottom - baseTop });
    } else {
      const r = (cur as Element).getBoundingClientRect();
      if (r.height > 0) rects.push({ top: r.top - baseTop, bottom: r.bottom - baseTop });
    }
    cur = walker.nextNode();
  }
  if (rects.length === 0) return [];
  rects.sort((a, b) => a.top - b.top || a.bottom - b.bottom);
  const lines: Array<{ top: number; bottom: number }> = [];
  for (const r of rects) {
    const last = lines[lines.length - 1];
    if (last && r.top < last.bottom - 0.5) {
      last.top = Math.min(last.top, r.top);
      last.bottom = Math.max(last.bottom, r.bottom);
    } else lines.push({ ...r });
  }
  const out: number[] = [];
  for (let i = 0; i < lines.length - 1; i++) out.push(round2((lines[i]!.bottom + lines[i + 1]!.top) / 2));
  out.push(round2(nodeBottom));
  return out;
}

export function readTableRows(el: HTMLElement, baseTop: number): TableRowGeometry[] {
  const table = el.querySelector("table.bp-table");
  if (!table) return [];
  const rows: TableRowGeometry[] = [];
  const read = (selector: string, kind: TableRowGeometry["kind"]) => {
    for (const tr of table.querySelectorAll<HTMLElement>(`:scope > ${selector} > tr`)) {
      const r = tr.getBoundingClientRect();
      rows.push({ top: round2(r.top - baseTop), height: round2(r.height), kind });
    }
  };
  read("thead", "header");
  read("tbody", "body");
  read("tfoot", "footer");
  return rows;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
